import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { useAuthStore, useCartStore } from '@/lib/store'
import { Toast } from '@/components/ui/toast'
import { ReviewForm } from '@/components/reviews/review-form'
import { ReviewList } from '@/components/reviews/review-list'
import { useTranslation } from 'react-i18next'
import { BackButton } from '@/components/ui/back-button'
import {
  DollarSign,
  Truck,
  MapPin,
  Clock,
  ShoppingCart,
  User,
  Star,
  Tag,
  AlertCircle,
  Badge,
  Shield,
  Heart,
  ChevronDown,
  Check,
  Globe
} from 'lucide-react'
import { CurrencyCode, CURRENCY_NAMES, convertCurrency, formatCurrency, getExchangeRate } from '@/lib/currency'

const renderDeliveryTypes = (deliveryTypes: Record<string, boolean>, t: any) => {
  return Object.entries(deliveryTypes)
    .filter(([_, enabled]) => enabled)
    .map(([type]) => (
      <div key={type} className="flex items-center p-3 sm:p-4 bg-blue-50 rounded-lg text-sm">
        <Truck className="h-4 w-4 text-primary mr-2 flex-shrink-0" />
        <span className="text-sm text-gray-900 capitalize">
          {type === 'standard' ? t('products.standard') :
           type === 'express' ? t('products.express') : t('products.pickup')}
        </span>
      </div>
    ))
}

export function ProductDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const addToCart = useCartStore((state) => state.addItem)
  const [product, setProduct] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [canReview, setCanReview] = useState(false)
  const [activeTab, setActiveTab] = useState<'details' | 'reviews'>('details')
  const [showLoginPrompt, setShowLoginPrompt] = useState(false)
  const [isWishlisted, setIsWishlisted] = useState(false)
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyCode>('USD')
  const [showCurrencySelector, setShowCurrencySelector] = useState(false)
  const { t } = useTranslation()
  
  // For size selection
  const [selectedSize, setSelectedSize] = useState<string | null>(null)
  
  // For color selection
  const [selectedColor, setSelectedColor] = useState<string | null>(null)

  useEffect(() => {
    fetchProduct()
    if (user) {
      checkCanReview()
      checkWishlistStatus()
    }
  }, [id, user])

  const fetchProduct = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          seller:profiles!seller_id (
            id,
            full_name,
            email,
            avatar_url,
            status,
            rating,
            is_verified,
            seller_stores (
              id,
              name,
              description
            )
          )
        `)
        .eq('id', id)
        .single()

      if (error) throw error

      // Only show active products or seller's own products
      if (data.status !== 'active' && data.seller_id !== user?.id) {
        navigate('/products')
        return
      }

      setProduct(data)
      if (data.images?.length > 0) {
        setSelectedImage(data.images[0])
      }
      
      // If there are sizes, select the first one by default
      if (data.sizes && data.sizes.length > 0) {
        setSelectedSize(data.sizes[0])
      }
      
      // If there are colors, select the first one by default
      if (data.colors && data.colors.length > 0) {
        setSelectedColor(data.colors[0])
      }
    } catch (err) {
      console.error('Error fetching product:', err)
      navigate('/products')
    } finally {
      setLoading(false)
    }
  }

  const checkCanReview = async () => {
    if (!user || !id) return

    try {
      // Check if user has bought the product
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select('id')
        .eq('buyer_id', user.id)
        .eq('product_id', id)
        .eq('status', 'delivered')

      if (ordersError) throw ordersError

      // Check if user has already reviewed
      const { data: reviews, error: reviewsError } = await supabase
        .from('reviews')
        .select('id')
        .eq('reviewer_id', user.id)
        .eq('target_type', 'product')
        .eq('target_id', id)

      if (reviewsError) throw reviewsError

      // Can review if has bought and not reviewed yet
      setCanReview(orders.length > 0 && reviews.length === 0)
    } catch (err) {
      console.error('Error checking review status:', err)
    }
  }

  const checkWishlistStatus = async () => {
    if (!user || !id) return

    try {
      const { data, error } = await supabase
        .from('wishlists')
        .select('id')
        .eq('user_id', user.id)
        .eq('product_id', id)
        .maybeSingle()

      if (error) throw error
      setIsWishlisted(!!data)
    } catch (err) {
      console.error('Error checking wishlist status:', err)
    }
  }

  const toggleWishlist = async () => {
    if (!user) {
      setShowLoginPrompt(true)
      return
    }

    try {
      if (isWishlisted) {
        // Remove from wishlist
        const { error } = await supabase
          .from('wishlists')
          .delete()
          .eq('user_id', user.id)
          .eq('product_id', id)

        if (error) throw error
        setIsWishlisted(false)
        setToastMessage('Retiré des favoris')
      } else {
        // Add to wishlist
        const { error } = await supabase
          .from('wishlists')
          .insert([{ user_id: user.id, product_id: id }])

        if (error) throw error
        setIsWishlisted(true)
        setToastMessage('Ajouté aux favoris')
      }
      setToastType('success')
      setShowToast(true)
    } catch (err) {
      console.error('Error updating wishlist:', err)
      setToastMessage('Une erreur est survenue')
      setToastType('error')
      setShowToast(true)
    }
  }

  const handleAddToCart = () => {
    if (!product) return

    // Create a new product object with the selected size and color if applicable
    const productToAdd = {
      ...product,
      selectedSize: selectedSize,
      selectedColor: selectedColor
    };
      
    addToCart(productToAdd)
    setToastMessage(t('products.addedToCart'))
    setToastType('success')
    setShowToast(true)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center px-4">
          <h2 className="text-xl font-semibold text-gray-900">{t('common.notFound')}</h2>
          <p className="mt-2 text-gray-500">{t('products.notFound')}</p>
          <Link to="/products">
            <Button variant="primary" className="mt-4">
              {t('common.back')}
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  // Get the converted price based on the selected currency
  const convertedPrice = convertCurrency(product.price, selectedCurrency);

  // Helper function to render color swatch
  const renderColorSwatch = (colorValue: string) => {
    // Handle special cases
    if (colorValue === 'multicolor') {
      return (
        <div className="h-6 w-6 rounded-full border border-gray-300 bg-gradient-to-r from-red-500 via-green-500 to-blue-500"></div>
      )
    } else if (colorValue === 'other') {
      return (
        <div className="h-6 w-6 rounded-full border border-gray-300 bg-gray-300 flex items-center justify-center">
          <span className="text-xs text-gray-600">?</span>
        </div>
      )
    }
    
    // Regular color display
    return (
      <div 
        className="h-6 w-6 rounded-full border border-gray-300" 
        style={{ backgroundColor: colorValue }}
      ></div>
    )
  }

  // Helper function to get color name
  const getColorName = (colorValue: string) => {
    const colorMap: Record<string, string> = {
      '#000000': 'Noir',
      '#FFFFFF': 'Blanc',
      '#FF0000': 'Rouge',
      '#0000FF': 'Bleu',
      '#008000': 'Vert',
      '#FFFF00': 'Jaune',
      '#808080': 'Gris',
      '#A52A2A': 'Marron',
      '#FFC0CB': 'Rose',
      '#FFA500': 'Orange',
      '#800080': 'Violet',
      '#F5F5DC': 'Beige',
      '#FFD700': 'Doré',
      '#C0C0C0': 'Argenté',
      'multicolor': 'Multicolore',
      'other': 'Autre'
    };
    
    return colorMap[colorValue] || colorValue;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 sm:py-8">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Navigation */}
        <div className="mb-6">
          <BackButton to="/products" label={t('common.back')} />
        </div>

        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 p-4 sm:p-6 lg:p-8">
            {/* Images */}
            <div className="space-y-4">
              <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                <img
                  src={selectedImage || product.images?.[0]}
                  alt={product.title}
                  className="w-full h-full object-cover"
                />
              </div>
              {product.images && product.images.length > 1 && (
                <div className="grid grid-cols-4 gap-2 sm:gap-4">
                  {product.images.map((image: string, index: number) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(image)}
                      className={`aspect-square rounded-lg overflow-hidden bg-gray-100 border-2 transition-all ${
                        selectedImage === image ? 'border-primary ring-2 ring-primary/20' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${product.title} - Image ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">{product.title}</h1>
                  {product.status === 'pending' && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 mt-2 sm:mt-0">
                      <Clock className="h-3 w-3 mr-1" />
                      {t('common.pending')}
                    </span>
                  )}
                </div>
                <div className="mt-2 flex items-center text-sm text-primary">
                  <Tag className="h-4 w-4 mr-1 flex-shrink-0" />
                  {product.category}
                </div>
                
                {/* Price display with currency conversion */}
                <div className="mt-4 flex flex-col gap-2">
                  <div className="flex items-center">
                    <DollarSign className="h-6 w-6 text-amber-500 flex-shrink-0" />
                    <span className="text-2xl sm:text-3xl font-bold text-gray-900">{product.price}</span>
                  </div>
                  
                  {/* Currency selector - only show for in-person payments */}
                  <div className="mt-2">
                    <div className="relative">
                      <div onClick={() => setShowCurrencySelector(!showCurrencySelector)} 
                        className="flex items-center justify-between px-4 py-2 border border-gray-300 rounded-md cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                        <span className="text-sm font-medium">Afficher en devises multiples</span>
                        <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${showCurrencySelector ? 'transform rotate-180' : ''}`} />
                      </div>
                      
                      {showCurrencySelector && (
                        <div className="mt-2 p-4 bg-white border border-gray-200 rounded-lg shadow-md">
                          <div className="mb-4 flex items-start">
                            <Shield className="h-5 w-5 text-primary mt-0.5 flex-shrink-0 mr-2" /> 
                            <p className="text-sm text-gray-600">
                              Sélectionnez une devise pour voir l'équivalent pour les paiements en présentiel.
                            </p>
                          </div>
                          
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Devise
                            </label>
                            <select
                              value={selectedCurrency}
                              onChange={(e) => setSelectedCurrency(e.target.value as CurrencyCode)}
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                            >
                              <option value="USD">Dollar américain (USD)</option>
                              <option value="HTG">Gourde haïtienne (HTG)</option>
                              <option value="DOP">Peso dominicain (DOP)</option>
                              <option value="EUR">Euro (EUR)</option>
                              <option value="CAD">Dollar canadien (CAD)</option>
                              <option value="JMD">Dollar jamaïcain (JMD)</option>
                            </select>
                          </div>
                          
                          {selectedCurrency !== 'USD' && (
                            <div className="mt-4 px-4 py-3 bg-blue-50 rounded-lg border border-blue-100">
                              <div className="space-y-1">
                                <p className="text-sm font-medium text-gray-900">
                                  Prix : {formatCurrency(product.price, 'USD')}
                                </p>
                                <p className="text-sm font-medium text-gray-900">
                                  Équivalent : {formatCurrency(convertedPrice, selectedCurrency)}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Taux : {getExchangeRate(selectedCurrency)}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {product.min_price && (
                    <div className="text-sm text-amber-600 font-medium bg-amber-50 px-3 py-1 rounded-full">
                      {t('products.minPrice')}: ${product.min_price}
                    </div>
                  )}
                </div>
              </div>

              {/* Seller Info */}
              <div className="border-t border-b border-gray-200 py-4">
                <div className="flex items-center">
                  <div className="h-12 w-12 flex-shrink-0">
                    {product.seller?.avatar_url ? (
                      <img
                        src={product.seller.avatar_url}
                        alt={product.seller.full_name}
                        className="h-12 w-12 rounded-full object-cover border-2 border-gray-200"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="h-6 w-6 text-gray-500" />
                      </div>
                    )}
                  </div>
                  <div className="ml-4 flex-1 min-w-0">
                    <div className="flex items-center">
                      <Link to={`/seller/${product.seller_id}`} className="text-sm font-medium text-gray-900 truncate hover:text-primary">
                        {product.seller?.seller_stores?.[0]?.name || t('products.seller')}
                      </Link>
                      {product.seller?.is_verified && (
                        <div className="ml-1.5 flex-shrink-0 flex">
                          <Badge className="h-4 w-4 text-blue-500" title="Vendeur vérifié" />
                        </div>
                      )}
                    </div>
                    {product.seller?.status === 'active' && (
                      <div className="mt-1 flex items-center">
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`h-4 w-4 ${
                                i < Math.floor(product.seller?.rating || 0)
                                  ? 'text-yellow-400 fill-current'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="ml-2 text-sm text-gray-500">
                          {product.seller?.rating?.toFixed(1) || '0.0'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`${
                      activeTab === 'details'
                        ? 'border-primary text-primary font-semibold'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                  >
                    {t('products.productDetails')}
                  </button>
                  <button
                    onClick={() => setActiveTab('reviews')}
                    className={`${
                      activeTab === 'reviews'
                        ? 'border-primary text-primary font-semibold'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                  >
                    {t('products.reviews')}
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              {activeTab === 'details' ? (
                <>
                  {/* Color Selection */}
                  {product.colors && product.colors.length > 0 && (
                    <div className="bg-blue-50/50 p-5 rounded-lg">
                      <h3 className="text-sm font-medium text-gray-900 mb-2">Couleurs disponibles</h3>
                      <div className="mt-2 flex flex-wrap gap-3">
                        {product.colors.map((color: string) => (
                          <button
                            key={color}
                            onClick={() => setSelectedColor(color)}
                            className={`flex flex-col items-center space-y-1 p-1 ${
                              selectedColor === color 
                                ? 'ring-2 ring-primary rounded-lg' 
                                : ''
                            }`}
                          >
                            <div className="relative">
                              {renderColorSwatch(color)}
                              {selectedColor === color && (
                                <div className="absolute -top-1 -right-1 bg-primary text-white rounded-full w-4 h-4 flex items-center justify-center">
                                  <Check className="h-3 w-3" />
                                </div>
                              )}
                            </div>
                            <span className="text-xs">{getColorName(color)}</span>
                          </button>
                        ))}
                      </div>
                      {selectedColor && (
                        <div className="mt-3 flex items-center text-sm text-primary">
                          <Check className="h-4 w-4 mr-1" />
                          <span>Couleur sélectionnée: {getColorName(selectedColor)}</span>
                        </div>
                      )}
                    </div>
                  )}
                
                  {/* Sizes Information */}
                  {product.sizes && product.sizes.length > 0 && (
                    <div className="bg-blue-50/50 p-5 rounded-lg">
                      <h3 className="text-sm font-medium text-gray-900 mb-2">Tailles disponibles</h3>
                      <div className="mt-2 grid grid-cols-4 gap-2">
                        {product.sizes.map((size: string) => (
                          <button
                            key={size}
                            type="button"
                            onClick={() => setSelectedSize(size)}
                            className={`py-2 px-3 border rounded-md text-sm font-medium ${
                              selectedSize === size
                                ? 'bg-primary text-white border-primary'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                      {selectedSize && (
                        <div className="mt-3 flex items-center text-sm text-primary">
                          <Check className="h-4 w-4 mr-1" />
                          <span>Taille sélectionnée: {selectedSize}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Description */}
                  <div className="bg-blue-50/50 p-5 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-900 mb-2">{t('products.description')}</h3>
                    <div className="mt-2 prose prose-sm text-gray-700 break-words">
                      {product.description}
                    </div>
                  </div>

                  {/* Delivery Options */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-3">{t('products.deliveryOptions')}</h3>
                    <div className="mt-2 space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
                        {renderDeliveryTypes(product.delivery_types || {}, t)}
                      </div>

                      {product.pickup_address && (
                        <div className="flex items-start text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                          <MapPin className="h-5 w-5 text-primary mr-2 flex-shrink-0" />
                          <span className="break-words">{product.pickup_address}</span>
                        </div>
                      )}

                      {product.estimated_delivery && (
                        <div className="flex items-center text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                          <Clock className="h-5 w-5 text-primary mr-2 flex-shrink-0" />
                          <span>{t('products.estimatedDelivery')}: {product.estimated_delivery} {t('products.days')}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Delivery Countries */}
                  {product.delivery_countries && product.delivery_countries.length > 0 && (
                    <div className="bg-blue-50/50 p-5 rounded-lg">
                      <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                        <Globe className="h-4 w-4 mr-2" />
                        Pays de livraison disponibles
                      </h3>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {product.delivery_countries.map((countryCode: string) => (
                          <span 
                            key={countryCode}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {getCountryName(countryCode)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-6">
                  {canReview && (
                    <ReviewForm
                      productId={product.id}
                      sellerId={product.seller_id}
                      onSuccess={() => {
                        setCanReview(false)
                        fetchProduct()
                      }}
                    />
                  )}

                  <ReviewList productId={product.id} />
                </div>
              )}

              {/* Actions */}
              <div className="pt-6 border-t">
                <div className="flex flex-wrap gap-2">
                  {product.status === 'active' ? (
                    <>
                      <Button
                        variant="primary"
                        className="w-full sm:w-auto flex-1 shadow-md hover:shadow-lg transition-shadow"
                        onClick={() => {
                          if (!user) {
                            setShowLoginPrompt(true);
                          } else {
                            handleAddToCart();
                          }
                        }}
                      >
                        <ShoppingCart className="h-5 w-5 mr-2 flex-shrink-0" />
                        {t('products.addToCart')}
                      </Button>
                      
                      <Button
                        variant={isWishlisted ? "outline" : "secondary"}
                        className={`${isWishlisted ? 'text-red-600 border-red-200 bg-red-50' : ''}`}
                        onClick={toggleWishlist}
                      >
                        <Heart className={`h-5 w-5 ${isWishlisted ? 'fill-current' : ''}`} />
                      </Button>
                      
                      {showLoginPrompt && !user && (
                        <div className="w-full mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <div className="flex items-start">
                            <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-yellow-800">Connexion requise</h3>
                              <div className="mt-2 text-sm text-yellow-700">
                                <p>Vous devez être connecté pour ajouter des produits au panier et effectuer des achats.</p>
                                <div className="mt-4 flex space-x-4">
                                  <Link to="/login" className="text-sm font-medium text-primary hover:text-primary-dark">
                                    Se connecter
                                  </Link>
                                  <Link to="/register" className="text-sm font-medium text-primary hover:text-primary-dark">
                                    Créer un compte
                                  </Link>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : user?.id === product.seller_id && (
                    <div className="w-full text-center text-sm text-gray-500 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                      <AlertCircle className="inline-block h-4 w-4 mr-1 text-yellow-500" />
                      {t('common.pending')}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Payment Security */}
              <div className="mt-4 bg-green-50 rounded-lg p-3 border border-green-100">
                <div className="flex items-center">
                  <Shield className="h-5 w-5 text-green-500 mr-2" />
                  <p className="text-sm text-green-700">
                    Paiement sécurisé et livraison fiable garantis
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper function to get country name from country code
function getCountryName(code: string): string {
  const countryMap: Record<string, string> = {
    'HT': 'Haïti',
    'DO': 'République Dominicaine',
    'US': 'États-Unis',
    'CA': 'Canada',
    'FR': 'France',
    'JM': 'Jamaïque',
    'CU': 'Cuba',
    'PR': 'Porto Rico',
    'BS': 'Bahamas',
    'BB': 'Barbade',
    'TT': 'Trinité-et-Tobago',
    'MX': 'Mexique'
  };
  
  return countryMap[code] || code;
}